/**
 * Account.jsx — plan, fees, capabilities, unlink.
 *
 * ─── Two honesty fixes over the first version ───────────────────────────────
 *
 * 1. The volume section was headed "THIS MONTH" while summing total_volume
 *    across every bot — which is LIFETIME. A user with six months of history
 *    saw it labelled monthly and then compared against a monthly break-even
 *    figure, so the comparison was meaningless. It now says ALL TIME.
 *
 * 2. Fees are derived client-side as volume × 1%, which assumes every trade
 *    was made on the free tier. Someone who was on a paid plan for part of
 *    that history paid 0% on those trades. The figure is therefore marked as
 *    an estimate rather than presented as a fact. A backend endpoint
 *    returning actual charged fees would replace this.
 *
 * ─── And a layout fix ───────────────────────────────────────────────────────
 * The first version was six near-identical label/value rows in three stacked
 * boxes: nothing drew the eye, and "fees paid" carried the same weight as
 * "taker fee rate". Fees are now the headline, break-even is a progress bar
 * rather than a paragraph, and the capability list is compressed.
 */

import React, { useEffect, useState } from "react";
import * as api from "../lib/api";
import * as tg from "../lib/telegram";
import { C, money, shortAddr, Label, Button, Empty, Chip, Skeleton } from "../theme";
import { useCountUp } from "../lib/motion";

const BREAK_EVEN = 3900;   // monthly volume where a $39 plan beats the 1% fee
const PLAN_PRICE = 39;

export default function Account({ onUnlinked }) {
  const [sub, setSub] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [bots, setBots] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, w, b] = await Promise.all([
          api.subscription().catch(() => null),
          api.balance().catch(() => null),
          api.listBots().catch(() => null),
        ]);
        setSub(s); setWallet(w);
        setBots(Array.isArray(b) ? b : b?.bots || []);
      } catch (e) { setErr(e.message); }
    })();
  }, []);

  // Derived before any early return so the hooks below always run.
  const list = bots || [];
  const volume = list.reduce((s, b) => s + Number(b.total_volume || 0), 0);
  const plan = (sub?.plan || sub?.tier || "free").toLowerCase();
  const isFree = plan === "free" || plan === "none";
  const feesPaid = isFree ? volume * 0.01 : 0;
  const pct = Math.min(100, (volume / BREAK_EVEN) * 100);

  const animFees = useCountUp(feesPaid);
  const animVolume = useCountUp(volume);

  const unlink = async () => {
    const ok = await tg.confirm(
      "Unlink this Telegram account? Your PolyEdge account is untouched.");
    if (!ok) return;
    setBusy(true);
    try {
      await api.unlink();
      tg.haptic.ok();
      onUnlinked();
    } catch (e) {
      tg.haptic.err(); setErr(e.message);
    } finally { setBusy(false); }
  };

  if (bots === null && !err) {
    return (
      <div style={{ padding: 14 }}>
        <Skeleton h={62} style={{ marginBottom: 14 }} />
        <Skeleton h={104} style={{ marginBottom: 14 }} />
        <Skeleton h={72} style={{ marginBottom: 14 }} />
        <Skeleton h={140} />
      </div>
    );
  }

  if (err && !sub && !wallet && !bots) {
    return <Empty><span style={{ color: C.rose }}>{err}</span></Empty>;
  }

  return (
    <div style={{ padding: 14 }}>

      {/* Identity */}
      <div style={{ border: `1px solid ${C.line}`, background: C.bg1,
                    padding: 12, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", marginBottom: 8, gap: 8 }}>
          <span style={{ fontSize: 13, color: C.ink, overflow: "hidden",
                         textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {sub?.username || tg.unsafeUser()?.username || "Account"}
          </span>
          <Chip color={isFree ? C.dim : C.green}>{plan.toUpperCase()}</Chip>
        </div>
        <p style={{ fontSize: 11, color: C.dim, margin: 0 }}>
          {shortAddr(wallet?.address || wallet?.eoa_address)}
        </p>
      </div>

      {/* Fees as the headline. It is the number that decides whether a plan
          is worth buying, so it should not sit in a row of equals. */}
      <div style={{ border: `1px solid ${isFree ? C.borderHi : C.border}`,
                    background: isFree ? "var(--pe-bg2, #12100c)" : C.bg1,
                    padding: 16, marginBottom: 16 }}>
        <Label style={{ marginBottom: 6 }}>
          {isFree ? "FEES PAID · ESTIMATE" : "FEES PAID"}
        </Label>
        <div style={{ fontFamily: C.disp, fontSize: 36, lineHeight: 1,
                      color: isFree ? C.amber : C.green, marginBottom: 10 }}>
          {money(animFees, false)}
        </div>
        <p style={{ fontSize: 10.5, color: C.dim, margin: 0, lineHeight: 1.65 }}>
          {isFree
            ? `1% taker fee on ${money(animVolume, false)} of lifetime volume. ` +
              `Estimated — trades made while on a paid plan were charged 0%.`
            : `Your plan removes the 1% taker fee.`}
        </p>
      </div>

      {/* Break-even as a bar. The prose version made people do the arithmetic;
          a filled bar answers "am I past it yet" at a glance. */}
      {isFree && (
        <div style={{ border: `1px solid ${C.line}`, background: C.bg1,
                      padding: 14, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between",
                        alignItems: "baseline", marginBottom: 9 }}>
            <span style={{ fontSize: 11.5, color: C.ink }}>
              Break-even vs a ${PLAN_PRICE} plan
            </span>
            <span style={{ fontSize: 11.5,
                           color: pct >= 100 ? C.green : C.dim }}>
              {pct.toFixed(0)}%
            </span>
          </div>
          <div style={{ height: 5, background: C.bg2, marginBottom: 9 }}>
            <div style={{ height: "100%", width: `${pct}%`,
                          background: pct >= 100 ? C.green : C.amber,
                          transition: "width 0.6s cubic-bezier(.22,1,.36,1)" }} />
          </div>
          <p style={{ fontSize: 10.5, color: C.dim, margin: 0, lineHeight: 1.65 }}>
            {pct >= 100
              ? `Past ${money(BREAK_EVEN, false)}/mo — a plan would now cost you ` +
                `less than the fee. Plans are managed at polyedge.online.`
              : `At ${money(BREAK_EVEN, false)}/mo of volume the 1% fee equals ` +
                `a $${PLAN_PRICE} plan. Below that, free is cheaper.`}
          </p>
        </div>
      )}

      <Label>ALL TIME</Label>
      <div style={{ border: `1px solid ${C.line}`, background: C.bg1,
                    marginBottom: 16 }}>
        <Line label="Volume traded" value={money(animVolume, false)} />
        <Line label="Bots" value={String(list.length)} />
        <Line last label="Taker fee rate" value={isFree ? "1.0%" : "0%"}
              color={isFree ? C.amber : C.green} />
      </div>

      {/* Capabilities compressed to two lines rather than five rows. The
          point is the boundary, not an inventory. */}
      <Label>WHAT TELEGRAM CAN DO</Label>
      <div style={{ border: `1px solid ${C.line}`, background: C.bg1,
                    padding: 12, marginBottom: 10 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap",
                      marginBottom: 10 }}>
          {["Bots", "P&L", "Create", "Stop", "Deposit"].map((x) => (
            <Chip key={x} color={C.green}>{x}</Chip>
          ))}
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["Withdraw", "Export key", "Plan changes"].map((x) => (
            <Chip key={x}>{x} · web</Chip>
          ))}
        </div>
      </div>
      <p style={{ fontSize: 10.5, color: C.dim, lineHeight: 1.7,
                  margin: "0 0 18px" }}>
        A Telegram session has no password and no second factor, so anything
        that moves funds out needs your account on the web.
      </p>

      {err && <p style={{ fontSize: 11.5, color: C.rose, margin: "0 0 12px" }}>{err}</p>}

      <Button variant="danger" onClick={unlink} disabled={busy}>
        {busy ? "Unlinking…" : "Unlink Telegram"}
      </Button>
    </div>
  );
}

const Line = ({ label, value, color, last }) => (
  <div style={{ display: "flex", justifyContent: "space-between",
                padding: "10px 12px", gap: 10,
                borderBottom: last ? "none" : `1px solid var(--pe-line, rgba(245,166,35,0.10))` }}>
    <span style={{ fontSize: 12, color: C.dim }}>{label}</span>
    <span style={{ fontSize: 12, color: color || C.ink }}>{value}</span>
  </div>
);
